import {
  closeSnackbar,
  enqueueSnackbar,
  type OptionsObject,
  type SnackbarKey,
} from "notistack";
import { captureError } from "./analytics.ts";

type NotifyErrorOptions = Omit<OptionsObject, "variant"> & {
  variant: "error";
  /** The caught error object — captured in analytics */
  error?: unknown;
  /** Skip the automatic `captureError` call (e.g. when the caller already captured) */
  skipAnalytics?: boolean;
};

type NotifyStandardOptions = Omit<OptionsObject, "variant"> & {
  variant?: Exclude<OptionsObject["variant"], "error">;
};

export type NotifyOptions = NotifyErrorOptions | NotifyStandardOptions;

/**
 * Show a snackbar notification.
 *
 * When `variant: "error"`, also fires `captureError` to analytics
 * using `message` as the error source.
 */
export function notify(message: string, options?: NotifyOptions): SnackbarKey {
  if (options?.variant === "error") {
    const { error, skipAnalytics, ...snackbarOptions } =
      options as NotifyErrorOptions;

    if (!skipAnalytics) {
      captureError(message, error ?? message);
    }

    return enqueueSnackbar(message, snackbarOptions);
  }

  return enqueueSnackbar(message, options as OptionsObject);
}

/** Programmatically dismiss a notification by key (or all if omitted). */
export function dismissNotification(key?: SnackbarKey): void {
  closeSnackbar(key);
}

// --- Network error notifications ---

const ERROR_NOTIFICATION_COOLDOWN = 10000; // 10 seconds cooldown
let lastErrorNotification = 0;

/** Show a cooldown-throttled error notification for a failed HTTP Response. */
export function notifyNetworkError(response: Response): void {
  const now = Date.now();

  if (now - lastErrorNotification < ERROR_NOTIFICATION_COOLDOWN) {
    return;
  }

  lastErrorNotification = now;

  notifyFetchError(`${response.status}: ${response.statusText} \n${response.url}`);
}

/** Show a pre-formatted fetch error notification (no analytics — callers handle that). */
export function notifyFetchError(message: string): void {
  notify(message, {
    variant: "error",
    skipAnalytics: true,
    style: { whiteSpace: "pre-line" },
  });
}

export type { SnackbarKey };
